import {
  useState,
  useEffect,
  ReactNode,
  FC,
  ChangeEvent,
  RefObject,
} from 'react';
import {
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
} from '@mui/material';
import { Search, Clear, FilterList } from '@mui/icons-material';
import { useDebounce } from 'use-debounce';
import { Button } from '@legacy/components/mui-components';
import { useSelectedFilterListInArray } from '@legacy/stores/FilterStore';
import { useSidePanelDispatch } from '@legacy/components/layout/SidePanel';
import { useTranslation } from 'react-i18next';
import {
  IViewOptionsField,
  ViewOptionsChangeParameters,
} from '@legacy/components/layout/FilterPanelLayout/types';
import { TViewOptions } from '@legacy/apis/savedViewAPI/types';
import { ViewOptions } from '../ViewOptions';

interface FilterSectionProps {
  changedViewOptions?: TViewOptions;
  onViewOptionsChange: (options: { name: string; value: string }[]) => void;
  viewOptionsLeftFilter?: ReactNode;
  viewOptionsFields?: Array<IViewOptionsField>;
  viewOptionsChange?: (item: ViewOptionsChangeParameters[]) => void;
  toolbarAction?: ReactNode;
  activateSidePanelButtonRef?: RefObject<HTMLButtonElement | null>;
  clearRowError?: () => void;
}

export const FilterSection: FC<FilterSectionProps> = ({
  changedViewOptions,
  onViewOptionsChange,
  viewOptionsLeftFilter,
  viewOptionsFields,
  viewOptionsChange,
  toolbarAction,
  activateSidePanelButtonRef,
  clearRowError,
}) => {
  const { t } = useTranslation('filter');

  // Filter sidebar
  const selectedFilterList = useSelectedFilterListInArray();
  const sidePanelDispatch = useSidePanelDispatch();

  const toggleFilterPanel = () => {
    clearRowError?.();
    sidePanelDispatch?.({ type: 'TOGGLE_PANEL' });
  };
  // Text search
  const [searchValue, setSearchValue] = useState(
    changedViewOptions?.['search-text'] || null,
  );
  const [debouncedSearchValue] = useDebounce(searchValue, 250);
  const [isInternalChange, setIsInternalChange] = useState(false);

  useEffect(() => {
    if (!isInternalChange) {
      setSearchValue(changedViewOptions?.['search-text'] || '');
    }
    setIsInternalChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedViewOptions?.['search-text']]);

  useEffect(() => {
    if (debouncedSearchValue !== changedViewOptions?.['search-text']) {
      setIsInternalChange(true);
      onViewOptionsChange?.([
        { name: 'search-text', value: debouncedSearchValue || '' },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearRowError?.();
    setSearchValue(event.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    clearRowError?.();
    onViewOptionsChange?.([{ name: 'search-text', value: '' }]);
  };

  return (
    <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ mr: 'auto', width: '100%' }}
      >
        {viewOptionsLeftFilter && viewOptionsLeftFilter}
        <TextField
          variant="outlined"
          size="small"
          value={searchValue ?? ''}
          onChange={handleSearchChange}
          title={t('FilterFullTextSearchText')}
          placeholder={t('FilterSearchPlaceholderText')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear text"
                    onClick={handleSearchClear}
                    edge="end"
                    size="small"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ ml: 'auto' }}
        />
      </Stack>

      <ViewOptions
        viewOptionsFields={viewOptionsFields}
        viewOptionsChange={viewOptionsChange}
      />

      <Button
        variant="text"
        startIcon={
          <Badge
            badgeContent={selectedFilterList.length}
            color="primary"
            data-automation-id="FilterContainerAddFilterCount"
          >
            <FilterList />
          </Badge>
        }
        onClick={toggleFilterPanel}
        ref={activateSidePanelButtonRef}
        data-automation-id="FilterContainerAddFilter"
      >
        {t('ShowActiveFiltersButton')}
      </Button>
      {toolbarAction && toolbarAction}
    </Stack>
  );
};
